/**
 * Create User Modal
 * Form to create a new organization member
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { MultiSelect } from '../../components/shared/MultiSelect/MultiSelect';
import userService, { CreateMemberRequest } from '../../services/api/userService';
import roleService, { Role } from '../../services/api/roleService';
import { Mail, User, Lock } from 'lucide-react';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  display: block;
`;

const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.status.error.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

export const CreateUserModal = ({ isOpen, onClose }: CreateUserModalProps) => {
  const queryClient = useQueryClient();
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateMemberRequest>();

  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => roleService.getRoles({ limit: 50 }),
    enabled: isOpen,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateMemberRequest) => userService.createMember(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      reset();
      setSelectedRoleIds([]);
      onClose();
    },
  });

  const onSubmit = (data: CreateMemberRequest) => {
    createMutation.mutate({
      ...data,
      roleIds: selectedRoleIds.length > 0 ? selectedRoleIds : undefined,
    });
  };

  const handleClose = () => {
    reset();
    setSelectedRoleIds([]);
    createMutation.reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New User"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary-blue"
            onClick={handleSubmit(onSubmit)}
            isLoading={createMutation.isPending}
          >
            Create User
          </Button>
        </>
      }
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        {createMutation.isError && (
          <ErrorMessage>
            {(createMutation.error as any)?.response?.data?.message ||
              'Failed to create user. Please try again.'}
          </ErrorMessage>
        )}

        <Input
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Invalid email address',
            },
          })}
          type="email"
          label="Email"
          placeholder="user@example.com"
          leftIcon={<Mail />}
          error={errors.email?.message}
          fullWidth
        />

        <Input
          {...register('firstName', {
            required: 'First name is required',
          })}
          type="text"
          label="First Name"
          placeholder="John"
          leftIcon={<User />}
          error={errors.firstName?.message}
          fullWidth
        />

        <Input
          {...register('lastName', {
            required: 'Last name is required',
          })}
          type="text"
          label="Last Name"
          placeholder="Doe"
          leftIcon={<User />}
          error={errors.lastName?.message}
          fullWidth
        />

        <Input
          {...register('password', {
            required: 'Password is required',
            minLength: {
              value: 8,
              message: 'Password must be at least 8 characters',
            },
          })}
          type="password"
          label="Temporary Password"
          placeholder="••••••••"
          leftIcon={<Lock />}
          error={errors.password?.message}
          helperText="User will be prompted to change password on first login"
          fullWidth
        />

        <FieldGroup>
          <Label>Roles (optional)</Label>
          <MultiSelect<Role>
            items={rolesData?.data ?? []}
            selectedIds={selectedRoleIds}
            onChange={setSelectedRoleIds}
            getItemId={(role) => role.id}
            getItemLabel={(role) => role.name}
            placeholder="Select roles..."
            searchable={false}
          />
        </FieldGroup>
      </Form>
    </Modal>
  );
};
